import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';

const BANK_DOCUMENTS_ERROR_MESSAGES: Record<string, string> = {
  BANK_DOCUMENT_DUPLICATE_FILE_HASH:
    'Ten plik CSV został już wcześniej zaimportowany. Wybierz inny plik lub sprawdź listę importów.',
  BANK_DOCUMENT_DELETE_CONTAINS_POSTED_ENTRIES:
    'Nie można usunąć importu, ponieważ zawiera zaksięgowane pozycje. Najpierw cofnij księgowanie tych pozycji.',
  BANK_DOCUMENT_DELETE_CONFLICT_SUPPLIER_PREPAYMENT:
    'Nie można usunąć importu, ponieważ część pozycji jest powiązana z rozliczeniami przedpłat dostawcy. W sekcji Pozycje dokumentu cofnij księgowanie tych pozycji lub usuń przypisanie.',
  BANK_DOCUMENT_ENTRY_ALREADY_POSTED_FOR_EDIT:
    'Nie można edytować pozycji, która została już zaksięgowana.',
  BANK_DOCUMENT_CSV_INVALID_FORMAT:
    'Plik CSV ma nieprawidłowy format lub niepoprawne dane. Sprawdź kolumny i wartości.',
  SUPPLIER_PREPAYMENT_INSUFFICIENT_OPEN_BALANCE:
    'Nie można cofnąć powiązań prepayment, ponieważ część środków została już rozliczona. Najpierw zwolnij alokacje prepayment na fakturach kosztowych.',
  BANK_ENTRY_TARGET_MISSING:
    'Pozycja nie ma wymaganego przypisania (Invoice lub Reservation lub FV kosztowa).',
  BANK_ENTRY_TARGET_CONFLICT: 'Pozycja ma konflikt przypisania. Wybierz tylko jeden target.',
  BANK_ENTRY_ALREADY_POSTED: 'Ta pozycja została już wcześniej zaksięgowana.',
  BANK_ENTRY_BOOKING_ID_FORBIDDEN: 'Kolumna BookingId nie jest wspierana w imporcie bankowym.',
  BANK_ENTRY_TARGET_NOT_FOUND: 'Wskazany target płatności nie istnieje w systemie.',
  BANK_ENTRY_ALLOCATION_MISSING:
    'Dla dodatniej pozycji trzeba wskazać co najmniej jedną alokację do faktury lub rezerwacji.',
  BANK_ENTRY_ALLOCATION_INCOMPLETE:
    'Suma alokacji nie domyka kwoty pozycji bankowej. Uzupełnij brakujące przypisanie.',
  BANK_ENTRY_ALLOCATION_MIXED_TARGET_TYPES:
    'W jednej pozycji mozna alokowac tylko do jednego typu targetu: albo faktury, albo rezerwacje.',
  BANK_ENTRY_ALLOCATION_SUM_MISMATCH:
    'Suma alokacji jest niezgodna z kwota pozycji bankowej.',
  BANK_ENTRY_ALLOCATION_INVALID_ROW:
    'Jedna z alokacji ma nieprawidlowy target albo nieprawidlowa kwote.',
  BANK_ENTRY_ALLOCATION_EXCEEDS_OPEN_AMOUNT:
    'Jedna z alokacji przekracza pozostala kwote do rozliczenia na wybranym celu.',
  BANK_ENTRY_ALLOCATION_EXCEEDS_PAID_AMOUNT:
    'Kwota zwrotu przekracza sumę wpłat netto na wybranej rezerwacji. Zmniejsz kwotę alokacji dla tej rezerwacji.',
  BANK_ENTRY_DIRECTION_CONFLICT:
    'Kierunek kwoty nie jest zgodny z obsługiwanym przepływem księgowania.',
  BANK_ENTRY_NOT_POSTED: 'Ta pozycja nie jest zaksięgowana i nie można cofnąć księgowania.',
  BANK_ENTRY_PREPAYMENT_NOT_ALLOWED: 'Dla tej pozycji nie można utworzyć zaliczki automatycznie.',
  BANK_ENTRY_EXPENSE_TARGET_NOT_FOUND: 'Nie znaleziono faktury kosztowej dla wskazanej pozycji.',
  BANK_ENTRY_EXPENSE_DIRECTION_CONFLICT:
    'Kierunek kwoty nie zgadza się z przypisaniem do faktury kosztowej.',
  BANK_ENTRY_EXPENSE_ALREADY_POSTED: 'Płatność kosztowa dla tej pozycji jest już zaksięgowana.',
  BANK_ENTRY_EXPENSE_NOT_PAYABLE: 'Wskazana faktura kosztowa nie jest gotowa do płatności.',
  BANK_ENTRY_POSTING_CONCURRENCY_CONFLICT:
    'Pozycja została zmieniona równolegle. Odśwież kolejkę i ponów operację.',
  BANK_ENTRY_POSTING_IDEMPOTENCY_CONFLICT:
    'Wykryto konflikt klucza idempotency dla tej operacji. Odśwież dane i spróbuj ponownie.',
  BANK_ENTRY_POSTING_IDEMPOTENCY_KEY_MISSING:
    'Brakuje klucza idempotency. Odśwież stronę i ponów operację.',
  BANK_ENTRY_POSTING_IDEMPOTENCY_KEY_INVALID:
    'Klucz idempotency ma nieprawidłowy format. Odśwież stronę i ponów operację.',
  BANK_ENTRY_UNPOST_SNAPSHOT_MISSING:
    'Nie można odtworzyć stanu do cofnięcia księgowania. Skontaktuj się z administratorem.',
  UNEXPECTED_ERROR: 'Wystąpił nieoczekiwany błąd techniczny. Sprawdź logi i spróbuj ponownie.',
};

interface ProblemPayloadShape {
  errorCode?: unknown;
  detail?: unknown;
  errors?: unknown;
}

const getErrorStatus = (error: unknown): number | string | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return undefined;
};

const getProblemPayload = (error: unknown): ProblemPayloadShape | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  return data as ProblemPayloadShape;
};

const getProblemErrorCode = (error: unknown): string | undefined => {
  const payload = getProblemPayload(error);
  if (!payload || typeof payload.errorCode !== 'string' || !payload.errorCode.trim()) {
    return undefined;
  }

  return payload.errorCode.trim();
};

const getProblemMessage = (error: unknown): string | undefined => {
  const payload = getProblemPayload(error);
  if (!payload) {
    return undefined;
  }

  if (typeof payload.errors === 'object' && payload.errors !== null) {
    const flattenedErrors = Object.values(payload.errors as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());

    if (flattenedErrors.length > 0) {
      return flattenedErrors.join('. ');
    }
  }

  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    return payload.detail.trim();
  }

  return undefined;
};

const getMessageFromStatus = (status: number | string | undefined): string | null => {
  if (status === undefined) {
    return null;
  }

  if (status === 'FETCH_ERROR') {
    return 'Brak połączenia z API. Sprawdź dostępność backendu i sieć.';
  }

  if (status === 'PARSING_ERROR') {
    return 'Odpowiedź API ma nieprawidłowy format.';
  }

  if (status === 400) {
    return 'Dane wejściowe są nieprawidłowe dla tej operacji.';
  }

  if (status === 401) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }

  if (status === 403) {
    return 'Brak uprawnień do operacji na dokumentach bankowych.';
  }

  if (status === 404) {
    return 'Nie znaleziono dokumentu lub pozycji bankowej.';
  }

  if (status === 409) {
    return 'Konflikt stanu dokumentu. Odśwież dane i spróbuj ponownie.';
  }

  if (status === 429) {
    return 'Zbyt wiele żądań. Odczekaj chwilę i ponów próbę.';
  }

  if (typeof status === 'number' && status >= 500) {
    return 'Wystąpił błąd serwera podczas operacji bankowej. Spróbuj ponownie później.';
  }

  return null;
};

export const getBankDocumentsErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const errorCode = getProblemErrorCode(error);
  if (errorCode && BANK_DOCUMENTS_ERROR_MESSAGES[errorCode]) {
    return BANK_DOCUMENTS_ERROR_MESSAGES[errorCode];
  }

  const problemMessage = getProblemMessage(error);
  if (problemMessage) {
    return problemMessage;
  }

  const statusMessage = getMessageFromStatus(getErrorStatus(error));
  if (statusMessage) {
    return statusMessage;
  }

  return extractApiErrorMessage(error, fallbackMessage);
};

export const getBankDocumentsErrorCode = (error: unknown): string | undefined => {
  return getProblemErrorCode(error);
};
